import { Injectable, Injector } from '@angular/core';
import {
  HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpErrorResponse,
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { PermissionDenialService } from '../services/permission-denial.service';
import { isPermissionForbidden, readDenial, isSilent403 } from '../utils/permission-denial.utils';
import { isExportSwitchForbidden, exportSwitchMessage } from '../utils/export-switch.utils';

/**
 * v1.6.1687 — Asana 1216892546346406: catch the permission 403 ONCE, centrally.
 *
 * The ticket's first instruction is to handle this in the shared client rather
 * than page by page, because any tenant endpoint can answer 403. Before this,
 * each screen had its own handling or none — and a page whose load never even
 * STARTS has no handler to reach (see permission-denial.utils.ts for how the
 * orders page manages that).
 *
 * THE ERROR IS RE-THROWN, never swallowed — the same rule the
 * BillingInterceptor follows next to it. A swallowed 403 would leave every
 * screen's spinner running forever, having reported success to nobody. The
 * interceptor only RECORDS; the screens keep their own handling.
 *
 * What it deliberately does not do:
 *
 *  - It does not touch auth. A 403 is NEVER a logout; only 401 ends a session,
 *    which the api-log interceptor already handles.
 *  - It does not claim a billing 403. Those carry `data.reason` and must keep
 *    showing the billing screen, so `isPermissionForbidden` excludes them.
 *  - It does not write its own copy. The server's message already names the
 *    missing permission; the ticket says to show it as sent.
 *  - It stays quiet for a permission PROBE, whose 403 is the answer rather
 *    than a fault, and would otherwise banner a page that is working.
 */
@Injectable()
export class PermissionInterceptor implements HttpInterceptor {
  /**
   * Injector rather than the service directly: an interceptor constructed at
   * app bootstrap must not pull in a graph that may itself depend on
   * HttpClient. Same reasoning as BillingInterceptor.
   */
  constructor(private injector: Injector) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(
      catchError((err: HttpErrorResponse) => {
        if (isPermissionForbidden(err) && !isSilent403(req.url)) {
          try {
            this.injector.get(PermissionDenialService).record(readDenial(err, req.url));
          } catch {
            // Never let the notice break the request pipeline.
          }
        } else if (isExportSwitchForbidden(err)) {
          // v1.6.1814 — Asana 1219882772481516. Exports switched off by the
          // platform team. `isPermissionForbidden` deliberately does not claim
          // this, so it is recorded as its own notice and the server's message
          // is shown as sent — it names who can switch them back on.
          try {
            this.injector.get(PermissionDenialService)
              .recordExportSwitch(exportSwitchMessage(err));
          } catch {
            // Never let the notice break the request pipeline.
          }
        }
        return throwError(() => err);
      }),
    );
  }
}
